import { useMemo, useState } from 'react';

import { ZfFormField, ZfInput, ZfListItem, ZfLoadingSpinner, ZfSelectOption } from '@zafire/react';
import { useField } from 'formik';

type SelectOption = {
  value: string;
  label: string;
};

type ZfSelectInputProps = {
  label: string;
  name: string;
  placeholder?: string;
  loading?: boolean;
  options: SelectOption[];
  searchInLabelAndValue?: boolean;
  mandatory?: boolean;
};

const ZfSelectInput = ({
  label,
  name,
  placeholder,
  options,
  loading = false,
  searchInLabelAndValue = false,
  mandatory = false,
}: ZfSelectInputProps) => {
  const [searchValue, setSearchValue] = useState('');

  const [field, meta, helpers] = useField(name);
  const { value } = field;
  const { error } = meta;
  const { setValue } = helpers;

  const optionMap = useMemo(() => new Map(options.map((option) => [option.value, option.label])), [options]);

  const suggestions = useMemo(() => {
    const search = searchValue.toLowerCase().trim();
    if (!search) {
      return options;
    }

    return options.filter((option) => {
      const labelMatch = option.label.toLowerCase().includes(search);

      if (!searchInLabelAndValue) {
        return labelMatch;
      }

      const valueMatch = option.value.toLowerCase().includes(search);

      return labelMatch || valueMatch;
    });
  }, [searchValue, options, searchInLabelAndValue]);

  return (
    <ZfFormField mandatory={mandatory} label={label} alerts={[error ?? '']}>
      <ZfInput
        {...field}
        data-testid={name}
        name={name}
        placeholder={placeholder}
        error={!!error}
        clearable
        type="select"
        allowSearch
        value={value || ''}
        onInputDropdownOpen={() => setSearchValue('')}
        onInputChange={(ev) => setSearchValue(ev.detail)}
        onSelectChange={(ev) => setValue(ev.detail as string)}
        getOptionLabel={(selectedValue) => optionMap.get(selectedValue) ?? ''}
      >
        {loading && (
          <>
            <ZfListItem description="Loading" size="small" variant="readonly" />
            <ZfLoadingSpinner slot="trailing" size="extra-small" color="primary" type="infinite" />
          </>
        )}

        {!loading &&
          suggestions.map((option) => (
            <ZfSelectOption
              key={option.value}
              value={option.value}
              description={option.label}
              descriptionHighlight={searchValue}
              isSelected={value === option.value}
            />
          ))}

        {!loading && suggestions.length === 0 && (
          <ZfListItem description="No results found" size="small" variant="readonly" />
        )}
      </ZfInput>
    </ZfFormField>
  );
};

export default ZfSelectInput;
